import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Chip, Tab, Tabs,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Alert, Stack, Select, MenuItem, FormControl, InputLabel,
  LinearProgress, Divider, Button, CircularProgress, Snackbar,
} from '@mui/material';
import {
  Assessment, EuroSymbol, LocationOn, Assignment, CheckCircle,
  ArrowUpward, ArrowDownward, Remove, PictureAsPdf,
  Park, Warning, ReportProblem, GppGood,
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as RechartTooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, CartesianGrid,
} from 'recharts';
import api, { measuresAPI } from '../services/api';

// ─── Types ───────────────────────────────────────────────────────────────────

interface Measure {
  id: string; measure_type: string; description: string;
  planned_date: string; completed_date: string; cost: number | null;
  status: string; baumnummer: string; baumart: string;
  ort: string; strasse: string;
  created_by_name: string; updated_by_name: string;
}

interface SavedLV {
  id: string; bezeichnung: string; ort: string; firma: string;
  zeitraum_von: string; zeitraum_bis: string; status: string;
  kosten_modus: string; gesamtkosten: number;
  anzahl_positionen: number; created_at: string; created_by_name: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmtEur = (n: number) => n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
const fmtDate = (d: string) => d ? new Date(d).toLocaleDateString('de-DE') : '–';
// Backend liefert numeric-Spalten oft als String ("100.50") – sicher zu Number umwandeln.
const toNum = (v: any): number => {
  if (v === null || v === undefined || v === '') return 0;
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  return isNaN(n) ? 0 : n;
};
const GREEN = '#4a7c59';
const VS_COLORS: Record<string, string> = {
  'Gegeben': '#2e7d32',
  'Nicht gegeben': '#c62828',
  'Zweifelhaft': '#f9a825',
  'Wiederherstellbar': '#e65100',
  'Ohne Angabe': '#90a4ae',
};
const STATUS_COLORS: Record<string, string> = {
  'offen': '#1565c0', 'in Arbeit': '#f9a825', 'erledigt': '#2e7d32', 'abgebrochen': '#90a4ae',
};
const COLORS = ['#4a7c59','#2d5a27','#7fb08a','#a8d5b5','#d4edda','#81c784','#388e3c','#1b5e20'];

// Maßnahmen gelten als "abgeschlossen", wenn ihr Status einer dieser Werte ist.
// Nach Migration v1.4.0 (TECH-001) sind die Status-Werte vereinheitlicht:
// 'offen', 'erledigt', 'abgebrochen' (CHECK-Constraint in tree_measures).
const isCompleted = (status?: string) => status === 'erledigt';

const statusConfig: Record<string, { label: string; color: string }> = {
  offen:         { label: 'Offen',         color: '#ed6c02' },
  beauftragt:    { label: 'Beauftragt',    color: '#1565c0' },
  abgeschlossen: { label: 'Abgeschlossen', color: '#2e7d32' },
};

function StatCard({ label, value, sub, color = GREEN, icon }: any) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Box sx={{ bgcolor: color + '15', color, p: 1, borderRadius: 2, mt: 0.3 }}>{icon}</Box>
        <Box>
          <Typography variant="caption" color="text.secondary">{label}</Typography>
          <Typography variant="h5" fontWeight={800} sx={{ color, lineHeight: 1.2 }}>{value}</Typography>
          {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
        </Box>
      </CardContent>
    </Card>
  );
}

function Delta({ pct }: { pct: number | null }) {
  if (pct === null) return null;
  const up = pct > 0; const neutral = pct === 0;
  return (
    <Chip size="small"
      icon={neutral ? <Remove sx={{ fontSize: 14 }} /> : up ? <ArrowUpward sx={{ fontSize: 14 }} /> : <ArrowDownward sx={{ fontSize: 14 }} />}
      label={`${pct > 0 ? '+' : ''}${pct.toFixed(1)}%`}
      sx={{ bgcolor: neutral ? '#f5f5f5' : up ? '#fff3e0' : '#e8f5e9', color: neutral ? '#666' : up ? '#e65100' : '#2e7d32', fontWeight: 700, fontSize: '0.7rem' }} />
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function Auswertung() {
  const [tab, setTab] = useState(0);
  const [measures, setMeasures] = useState<Measure[]>([]);
  const [savedLVs, setSavedLVs] = useState<SavedLV[]>([]);
  const [vsData, setVsData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [vonDate, setVonDate] = useState('');
  const [bisDate, setBisDate] = useState('');
  const [vergleichModus, setVergleichModus] = useState(false);
  const [von2, setVon2] = useState(''); const [bis2, setBis2] = useState('');
  const [lvStatusFilter, setLvStatusFilter] = useState('alle');
  const [lvOrtFilter, setLvOrtFilter] = useState('alle');

  // PDF-Export State (FEAT-018)
  const [pdfLoading, setPdfLoading] = useState(false);
  const [snackbar, setSnackbar] = useState('');

  // PDF-Download: triggert Blob-Download
  const downloadPdf = async (url: string, filename: string) => {
    setPdfLoading(true);
    try {
      const res = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      setSnackbar('PDF wurde erstellt');
    } catch (err: any) {
      setSnackbar('PDF konnte nicht erstellt werden: ' + (err.response?.status || 'Fehler'));
    } finally {
      setPdfLoading(false);
    }
  };

  const handlePdfMassnahmen = () => {
    const params = new URLSearchParams();
    if (vonDate) params.set('von', vonDate);
    if (bisDate) params.set('bis', bisDate);
    const qs = params.toString() ? '?' + params.toString() : '';
    downloadPdf(`/reports/measures/pdf${qs}`, `massnahmen-auswertung.pdf`);
  };

  const handlePdfLv = (lvId: string, bezeichnung: string) => {
    const safe = bezeichnung.replace(/[^\w\-]+/g, '_');
    downloadPdf(`/reports/lv/${lvId}/pdf`, `lv-${safe}.pdf`);
  };

  useEffect(() => {
    // Defensiver Fetch: Beide Calls einzeln behandeln, damit ein Fehler in einem
    // Endpoint den anderen nicht blockiert.
    const errors: string[] = [];

    api.get('/reports/verkehrssicherheit').then(r => setVsData(r.data)).catch(() => setVsData(null));

    Promise.allSettled([measuresAPI.getAll(), api.get('/lv')])
      .then(([mResult, lvResult]) => {
        if (mResult.status === 'fulfilled') {
          const data = mResult.value.data;
          setMeasures(Array.isArray(data) ? data : []);
        } else {
          errors.push('Maßnahmen konnten nicht geladen werden: ' +
            (mResult.reason?.response?.data?.error || mResult.reason?.message || 'Unbekannter Fehler'));
        }
        if (lvResult.status === 'fulfilled') {
          const data = lvResult.value.data;
          setSavedLVs(Array.isArray(data) ? data : []);
        } else {
          errors.push('Leistungsverzeichnisse konnten nicht geladen werden: ' +
            (lvResult.reason?.response?.data?.error || lvResult.reason?.message || 'Unbekannter Fehler'));
        }
        if (errors.length > 0) setLoadError(errors.join(' · '));
      })
      .finally(() => setLoading(false));
  }, []);

  const filterMeasures = (von: string, bis: string, data = measures) =>
    data.filter((m) => {
      // Zählt erledigte Maßnahmen ODER solche mit hinterlegten Kosten (eigene
      // oder aus der verknüpften LV-Position). So fließen geplante LV-Kosten in
      // die Auswertung ein, ohne dass die vielen kostenlosen Auto-Maßnahmen
      // (Status „offen", cost null) die Zahlen fluten.
      if (!isCompleted(m.status) && toNum(m.cost) <= 0) return false;
      const d = m.completed_date || m.planned_date;
      if (!d) return true;
      const dt = new Date(d);
      if (von && dt < new Date(von)) return false;
      if (bis && dt > new Date(bis + 'T23:59:59')) return false;
      return true;
    });

  const filtered1 = useMemo(() => filterMeasures(vonDate, bisDate), [measures, vonDate, bisDate]);
  const filtered2 = useMemo(() => filterMeasures(von2, bis2), [measures, von2, bis2]);

  const statsFor = (list: Measure[]) => ({
    count: list.length,
    kosten: list.reduce((s, m) => s + toNum(m.cost), 0),
    orte: new Set(list.map(m => m.ort).filter(Boolean)).size,
    typen: new Set(list.map(m => m.measure_type).filter(Boolean)).size,
  });
  const s1 = useMemo(() => statsFor(filtered1), [filtered1]);
  const s2 = useMemo(() => statsFor(filtered2), [filtered2]);
  // Gesamtkosten ALLER LVs (ungefiltert) – aussagekräftige Geld-Zahl auch bei
  // Pauschal-LVs, wo keine Kosten je Maßnahme vorliegen.
  const lvKostenGesamt = useMemo(
    () => savedLVs.reduce((s, l) => s + (parseFloat(String(l.gesamtkosten || 0)) || 0), 0),
    [savedLVs]
  );
  const pct = (a: number, b: number) => b === 0 ? null : Math.round(((a - b) / b) * 1000) / 10;

  const zeitverlauf = useMemo(() => {
    const byMonth: Record<string, { monat: string; z1: number; z2: number }> = {};
    const addToMap = (list: Measure[], key: 'z1' | 'z2') => {
      list.forEach((m) => {
        const d = new Date(m.completed_date || m.planned_date || '');
        if (isNaN(d.getTime())) return;
        const k = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
        if (!byMonth[k]) byMonth[k] = { monat: k, z1: 0, z2: 0 };
        byMonth[k][key] += toNum(m.cost);
      });
    };
    addToMap(filtered1, 'z1'); addToMap(filtered2, 'z2');
    return Object.values(byMonth).sort((a, b) => a.monat.localeCompare(b.monat));
  }, [filtered1, filtered2]);

  // Kosten pro Bauhof-Mitarbeiter: aus Bauhof-LVs, Kosten je LV gleichmäßig auf die
  // genannten Mitarbeiter aufgeteilt (so summiert es sich sauber zum Bauhof-Gesamt).
  const byBearbeiter = useMemo(() => {
    const map: Record<string, number> = {};
    savedLVs.forEach((l: any) => {
      if (l.auftrag_art !== 'bauhof') return;
      const kosten = parseFloat(String(l.gesamtkosten || 0)) || 0;
      const namen = String(l.bauhof_mitarbeiter || '').split(',').map((s: string) => s.trim()).filter(Boolean);
      if (namen.length === 0) { map['(ohne Zuordnung)'] = (map['(ohne Zuordnung)'] || 0) + kosten; return; }
      const anteil = kosten / namen.length;
      namen.forEach((n: string) => { map[n] = (map[n] || 0) + anteil; });
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([name, kosten]) => ({ name, kosten }));
  }, [savedLVs]);

  const byTyp = useMemo(() => {
    const map: Record<string, number> = {};
    filtered1.forEach((m) => { map[m.measure_type||'Sonstige'] = (map[m.measure_type||'Sonstige']||0)+1; });
    return Object.entries(map).sort((a, b) => b[1]-a[1]).map(([name, anzahl]) => ({ name, anzahl }));
  }, [filtered1]);

  const byStatus = useMemo(() => {
    const map: Record<string, number> = {};
    filtered1.forEach((m) => { const k = m.status || 'offen'; map[k] = (map[k]||0)+1; });
    const order = ['offen', 'in Arbeit', 'erledigt', 'abgebrochen'];
    const known = order.filter(s => map[s]).map(s => ({ name: s, anzahl: map[s] }));
    const rest = Object.keys(map).filter(k => !order.includes(k)).map(k => ({ name: k, anzahl: map[k] }));
    return [...known, ...rest];
  }, [filtered1]);

  // Kosten pro Ort: aus den LV-Gesamtkosten je Ort (füllt auch bei Pauschal-LVs,
  // wo keine Kosten je Maßnahme vorliegen).
  const byOrt = useMemo(() => {
    const map: Record<string, number> = {};
    savedLVs.forEach((l: any) => { if (l.ort) map[l.ort] = (map[l.ort] || 0) + (parseFloat(String(l.gesamtkosten || 0)) || 0); });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 10).map(([ort, kosten]) => ({ ort, kosten }));
  }, [savedLVs]);

  // Intern (Bauhof) vs. extern (Firma) – Kostenaufteilung aus den LVs.
  const internExtern = useMemo(() => {
    let intern = 0, extern = 0;
    savedLVs.forEach((l: any) => {
      const k = parseFloat(String(l.gesamtkosten || 0)) || 0;
      if (l.auftrag_art === 'bauhof') intern += k; else extern += k;
    });
    return { intern, extern, gesamt: intern + extern };
  }, [savedLVs]);

  // LV stats
  const filteredLVs = useMemo(() =>
    savedLVs.filter((lv) => {
      if (lvStatusFilter !== 'alle' && lv.status !== lvStatusFilter) return false;
      if (lvOrtFilter !== 'alle' && lv.ort !== lvOrtFilter) return false;
      return true;
    }), [savedLVs, lvStatusFilter, lvOrtFilter]);

  const lvOrte = useMemo(() => { const s = new Set<string>(); savedLVs.forEach(l => { if (l.ort) s.add(l.ort); }); return Array.from(s).sort(); }, [savedLVs]);

  const lvStats = useMemo(() => ({
    gesamt: filteredLVs.length,
    offen: filteredLVs.filter(l => l.status==='offen').length,
    beauftragt: filteredLVs.filter(l => l.status==='beauftragt').length,
    abgeschlossen: filteredLVs.filter(l => l.status==='abgeschlossen').length,
    kosten: filteredLVs.reduce((s, l) => s + (parseFloat(String(l.gesamtkosten||0))||0), 0),
    positionen: filteredLVs.reduce((s, l) => s + (parseInt(String(l.anzahl_positionen||0))||0), 0),
  }), [filteredLVs]);

  const lvByStatus = useMemo(() => [
    { name: 'Offen', anzahl: lvStats.offen, color: '#ed6c02' },
    { name: 'Beauftragt', anzahl: lvStats.beauftragt, color: '#1565c0' },
    { name: 'Abgeschlossen', anzahl: lvStats.abgeschlossen, color: '#2e7d32' },
  ].filter(d => d.anzahl > 0), [lvStats]);

  const lvKostenByOrt = useMemo(() => {
    const map: Record<string, number> = {};
    filteredLVs.forEach(lv => { if (lv.ort) map[lv.ort] = (map[lv.ort]||0) + (parseFloat(String(lv.gesamtkosten||0))||0); });
    return Object.entries(map).sort((a,b) => b[1]-a[1]).map(([ort, kosten]) => ({ ort, kosten }));
  }, [filteredLVs]);

  if (loading) return <LinearProgress sx={{ mt: 4 }} />;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Assessment sx={{ fontSize: 28, color: GREEN }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={700}>Auswertung</Typography>
          <Typography variant="body2" color="text.secondary">Maßnahmen & Leistungsverzeichnisse im Überblick</Typography>
        </Box>
        {/* PDF-Export nur in Tab 0 (Maßnahmen) – im LV-Tab erfolgt der Export pro LV in der Tabelle */}
        {tab === 0 && (
          <Button
            variant="outlined"
            startIcon={pdfLoading ? <CircularProgress size={16} /> : <PictureAsPdf />}
            onClick={handlePdfMassnahmen}
            disabled={pdfLoading}
            sx={{ borderColor: GREEN, color: GREEN, '&:hover': { borderColor: GREEN, bgcolor: GREEN + '10' } }}
          >
            PDF Export
          </Button>
        )}
      </Box>

      {loadError && (
        <Alert severity="warning" sx={{ mb: 2 }}>{loadError}</Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2.5 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}
          sx={{ '& .MuiTab-root.Mui-selected': { color: GREEN }, '& .MuiTabs-indicator': { bgcolor: GREEN } }}>
          <Tab label="📊 Maßnahmen-Auswertung" />
          <Tab label={`📁 Leistungsverzeichnisse (${savedLVs.length})`} />
          <Tab label="🛡️ Verkehrssicherheit" />
        </Tabs>
      </Box>

      {/* TAB 0: Maßnahmen */}
      {tab === 0 && (
        <Box>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Typography variant="subtitle2" fontWeight={700}>Zeitraum:</Typography>
                <input type="date" value={vonDate} onChange={(e) => setVonDate(e.target.value)}
                  style={{ padding:'6px 10px', border:'1px solid #ccc', borderRadius:6, fontSize:'0.85rem' }} />
                <Typography variant="caption">–</Typography>
                <input type="date" value={bisDate} onChange={(e) => setBisDate(e.target.value)}
                  style={{ padding:'6px 10px', border:'1px solid #ccc', borderRadius:6, fontSize:'0.85rem' }} />
                <Chip label={vergleichModus ? 'Vergleich aus' : 'Vergleich'} size="small"
                  variant={vergleichModus ? 'filled' : 'outlined'}
                  sx={{ cursor:'pointer', ...(vergleichModus && { bgcolor: GREEN, color:'white' }) }}
                  onClick={() => setVergleichModus(p => !p)} />
                {(vonDate||bisDate) && (
                  <Chip label="Filter löschen" size="small" variant="outlined" sx={{ cursor:'pointer' }}
                    onClick={() => { setVonDate(''); setBisDate(''); setVergleichModus(false); setVon2(''); setBis2(''); }} />
                )}
              </Box>
              {vergleichModus && (
                <Box sx={{ display:'flex', gap:1, alignItems:'center', mt:1.5 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ color:'#888' }}>Vergleich:</Typography>
                  <input type="date" value={von2} onChange={(e) => setVon2(e.target.value)}
                    style={{ padding:'6px 10px', border:'1px solid #ccc', borderRadius:6, fontSize:'0.85rem', opacity:0.75 }} />
                  <Typography variant="caption">–</Typography>
                  <input type="date" value={bis2} onChange={(e) => setBis2(e.target.value)}
                    style={{ padding:'6px 10px', border:'1px solid #ccc', borderRadius:6, fontSize:'0.85rem', opacity:0.75 }} />
                </Box>
              )}
            </CardContent>
          </Card>

          {vergleichModus && von2 && bis2 && (
            <Alert severity="info" sx={{ mb:2 }}>
              Vergleich: <strong>{vonDate||'–'} – {bisDate||'–'}</strong> vs. <strong>{von2} – {bis2}</strong>
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mb:2 }}>
            {[
              { label:'Maßnahmen (erledigt/mit Kosten)', v1:s1.count, v2:s2.count, fmt:(n:number)=>String(n), icon:<CheckCircle /> },
              { label:'Gesamtkosten (LVs)', v1:lvKostenGesamt, v2:lvKostenGesamt, fmt:fmtEur, icon:<EuroSymbol /> },
              { label:'Betroffene Orte', v1:s1.orte, v2:s2.orte, fmt:(n:number)=>String(n), icon:<LocationOn /> },
              { label:'Maßnahmentypen', v1:s1.typen, v2:s2.typen, fmt:(n:number)=>String(n), icon:<Assignment /> },
            ].map(({ label, v1, v2, fmt, icon }) => (
              <Grid item xs={6} md={3} key={label}>
                <StatCard label={label} value={fmt(v1)}
                  sub={vergleichModus && von2 ? `Vergl.: ${fmt(v2)}` : undefined} icon={icon} />
              </Grid>
            ))}
          </Grid>

          {internExtern.gesamt > 0 && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>Intern (Bauhof) vs. extern (Firma)</Typography>
                {[
                  { label: 'Intern (Bauhof)', wert: internExtern.intern, farbe: '#8d6e63' },
                  { label: 'Extern (Firma)', wert: internExtern.extern, farbe: GREEN },
                ].map(({ label, wert, farbe }) => (
                  <Box key={label} sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                      <Typography variant="caption" fontWeight={700}>
                        {fmtEur(wert)} · {Math.round(wert / internExtern.gesamt * 100)}%
                      </Typography>
                    </Box>
                    <Box sx={{ height: 8, borderRadius: 4, bgcolor: 'action.hover', overflow: 'hidden' }}>
                      <Box sx={{ height: '100%', width: `${wert / internExtern.gesamt * 100}%`, bgcolor: farbe }} />
                    </Box>
                  </Box>
                ))}
              </CardContent>
            </Card>
          )}

          {vergleichModus && von2 && (
            <Box sx={{ display:'flex', gap:1.5, mb:2, flexWrap:'wrap', alignItems:'center' }}>
              <Typography variant="caption" color="text.secondary">Veränderung:</Typography>
              <Typography variant="caption">Maßnahmen</Typography><Delta pct={pct(s1.count, s2.count)} />
              <Typography variant="caption">Kosten</Typography><Delta pct={pct(s1.kosten, s2.kosten)} />
            </Box>
          )}

          <Grid container spacing={2}>
            {zeitverlauf.length > 0 && (
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb:2 }}>Kosten pro Monat</Typography>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={zeitverlauf}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="monat" tick={{ fontSize:11 }} />
                        <YAxis tick={{ fontSize:11 }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k€`} />
                        <RechartTooltip formatter={(v: any) => fmtEur(v)} />
                        <Bar dataKey="z1" fill={GREEN} name="Zeitraum 1" radius={[3,3,0,0]} />
                        {vergleichModus && <Bar dataKey="z2" fill="#a8d5b5" name="Zeitraum 2" radius={[3,3,0,0]} />}
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {byBearbeiter.length > 0 && (
              <Grid item xs={12} md={6}>
                <Card sx={{ height:'100%' }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb:1.5 }}>Kosten pro Bauhof-Mitarbeiter</Typography>
                    <Stack spacing={1}>
                      {byBearbeiter.map(({ name, kosten }) => {
                        const max = byBearbeiter[0].kosten;
                        return (
                          <Box key={name}>
                            <Box sx={{ display:'flex', justifyContent:'space-between', mb:0.3 }}>
                              <Typography variant="caption">{name}</Typography>
                              <Typography variant="caption" fontWeight={700}>{fmtEur(kosten)}</Typography>
                            </Box>
                            <LinearProgress variant="determinate" value={(kosten/max)*100}
                              sx={{ height:6, borderRadius:3, bgcolor:'#e8f5e9', '& .MuiLinearProgress-bar':{ bgcolor:GREEN } }} />
                          </Box>
                        );
                      })}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {byTyp.length > 0 && (
              <Grid item xs={12} md={6}>
                <Card sx={{ height:'100%' }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb:1 }}>Maßnahmen nach Typ</Typography>
                    <ResponsiveContainer width="100%" height={180}>
                      <PieChart>
                        <Pie data={byTyp} dataKey="anzahl" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={2}>
                          {byTyp.map((_, i) => <Cell key={i} fill={COLORS[i%COLORS.length]} />)}
                        </Pie>
                        <Legend iconSize={10} wrapperStyle={{ fontSize:'0.75rem' }} />
                        <RechartTooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {byStatus.length > 0 && (
              <Grid item xs={12} md={6}>
                <Card sx={{ height:'100%' }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb:1 }}>Maßnahmen nach Status</Typography>
                    <ResponsiveContainer width="100%" height={180}>
                      <PieChart>
                        <Pie data={byStatus} dataKey="anzahl" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={2}
                          isAnimationActive animationDuration={900} animationBegin={150}>
                          {byStatus.map((s, i) => <Cell key={i} fill={STATUS_COLORS[s.name] || COLORS[i%COLORS.length]} />)}
                        </Pie>
                        <Legend iconSize={10} wrapperStyle={{ fontSize:'0.75rem' }} />
                        <RechartTooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {byOrt.length > 0 && (
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb:1.5 }}>Kosten pro Ort</Typography>
                    <Stack spacing={1}>
                      {byOrt.map(({ ort, kosten }) => {
                        const max = byOrt[0].kosten;
                        return (
                          <Box key={ort}>
                            <Box sx={{ display:'flex', justifyContent:'space-between', mb:0.3 }}>
                              <Typography variant="caption">{ort}</Typography>
                              <Typography variant="caption" fontWeight={700}>{fmtEur(kosten)}</Typography>
                            </Box>
                            <LinearProgress variant="determinate" value={(kosten/max)*100}
                              sx={{ height:6, borderRadius:3, bgcolor:'#e8f5e9', '& .MuiLinearProgress-bar':{ bgcolor:GREEN } }} />
                          </Box>
                        );
                      })}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {filtered1.length > 0 && (
              <Grid item xs={12}>
                <Card>
                  <CardContent sx={{ pb:'8px !important' }}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb:1.5 }}>
                      Detailtabelle ({filtered1.length}{filtered1.length>50?', zeige 50':''})
                    </Typography>
                    <TableContainer sx={{ maxHeight:320 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow sx={{ '& th':{ bgcolor:GREEN, color:'white', fontWeight:700, fontSize:'0.72rem' } }}>
                            <TableCell>Baum-Nr.</TableCell><TableCell>Ort</TableCell>
                            <TableCell>Maßnahme</TableCell><TableCell>Abgeschlossen</TableCell>
                            <TableCell>Bearbeiter</TableCell><TableCell align="right">Kosten</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filtered1.slice(0,50).map((m) => (
                            <TableRow key={m.id} hover>
                              <TableCell sx={{ fontWeight:700, fontSize:'0.78rem' }}>#{m.baumnummer}</TableCell>
                              <TableCell sx={{ fontSize:'0.75rem' }}>{m.ort||'–'}</TableCell>
                              <TableCell sx={{ fontSize:'0.78rem' }}>{m.measure_type}</TableCell>
                              <TableCell sx={{ fontSize:'0.75rem' }}>{fmtDate(m.completed_date||m.planned_date)}</TableCell>
                              <TableCell sx={{ fontSize:'0.75rem' }}>{m.updated_by_name||m.created_by_name||'–'}</TableCell>
                              <TableCell align="right" sx={{ fontSize:'0.78rem', fontWeight:600 }}>{m.cost?fmtEur(toNum(m.cost)):'–'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {filtered1.length === 0 && (
              <Grid item xs={12}>
                {(() => {
                  const total = measures.length;
                  const completedTotal = measures.filter((m) => isCompleted(m.status)).length;
                  if (total === 0) {
                    return <Alert severity="info">Es sind noch keine Maßnahmen erfasst.</Alert>;
                  }
                  if (completedTotal === 0) {
                    return (
                      <Alert severity="info">
                        {total} Maßnahmen vorhanden, aber keine ist als <strong>erledigt</strong> markiert.
                        Schließe Maßnahmen am Baum ab, um sie hier auszuwerten.
                      </Alert>
                    );
                  }
                  return (
                    <Alert severity="info">
                      Im gewählten Zeitraum keine abgeschlossenen Maßnahmen gefunden.
                      Insgesamt sind {completedTotal} abgeschlossene Maßnahme{completedTotal === 1 ? '' : 'n'} erfasst –
                      passe den Zeitraum an oder lösche den Filter.
                    </Alert>
                  );
                })()}
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      {/* TAB 1: Leistungsverzeichnisse */}
      {tab === 1 && (
        <Box>
          <Card sx={{ mb:2 }}>
            <CardContent>
              <Box sx={{ display:'flex', gap:2, flexWrap:'wrap', alignItems:'center' }}>
                <FormControl size="small" sx={{ minWidth:140 }}>
                  <InputLabel>Status</InputLabel>
                  <Select value={lvStatusFilter} label="Status" onChange={(e) => setLvStatusFilter(e.target.value)}>
                    <MenuItem value="alle">Alle Status</MenuItem>
                    {Object.entries(statusConfig).map(([key, cfg]) => (
                      <MenuItem key={key} value={key}>
                        <Typography sx={{ color:cfg.color, fontWeight:600, fontSize:'0.85rem' }}>{cfg.label}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth:160 }}>
                  <InputLabel>Ort</InputLabel>
                  <Select value={lvOrtFilter} label="Ort" onChange={(e) => setLvOrtFilter(e.target.value)}>
                    <MenuItem value="alle">Alle Orte</MenuItem>
                    {lvOrte.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                  </Select>
                </FormControl>
                {(lvStatusFilter !== 'alle' || lvOrtFilter !== 'alle') && (
                  <Chip label="Filter löschen" size="small" variant="outlined" sx={{ cursor:'pointer' }}
                    onClick={() => { setLvStatusFilter('alle'); setLvOrtFilter('alle'); }} />
                )}
              </Box>
            </CardContent>
          </Card>

          <Grid container spacing={2} sx={{ mb:2 }}>
            <Grid item xs={6} md={3}><StatCard label="LVs gesamt" value={String(lvStats.gesamt)} icon={<Assignment />} /></Grid>
            <Grid item xs={6} md={3}><StatCard label="Gesamtvolumen" value={fmtEur(lvStats.kosten)} icon={<EuroSymbol />} /></Grid>
            <Grid item xs={6} md={3}><StatCard label="Positionen gesamt" value={String(lvStats.positionen)} sub="verknüpfte Maßnahmen" icon={<CheckCircle />} /></Grid>
            <Grid item xs={6} md={3}>
              <StatCard label="Abgeschlossen" value={String(lvStats.abgeschlossen)}
                sub={`${lvStats.offen} offen · ${lvStats.beauftragt} beauftragt`}
                color="#2e7d32" icon={<CheckCircle />} />
            </Grid>
          </Grid>

          {savedLVs.length > 0 && (
            <Grid container spacing={2} sx={{ mb:2 }}>
              {lvByStatus.length > 1 && (
                <Grid item xs={12} md={4}>
                  <Card sx={{ height:'100%' }}>
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ mb:1 }}>Status-Verteilung</Typography>
                      <ResponsiveContainer width="100%" height={160}>
                        <PieChart>
                          <Pie data={lvByStatus} dataKey="anzahl" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={65} paddingAngle={2}>
                            {lvByStatus.map((d, i) => <Cell key={i} fill={d.color} />)}
                          </Pie>
                          <Legend iconSize={10} wrapperStyle={{ fontSize:'0.75rem' }} />
                          <RechartTooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>
              )}
              {lvKostenByOrt.length > 0 && (
                <Grid item xs={12} md={lvByStatus.length > 1 ? 8 : 12}>
                  <Card sx={{ height:'100%' }}>
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ mb:1.5 }}>Auftragsvolumen pro Ort</Typography>
                      <Stack spacing={1}>
                        {lvKostenByOrt.map(({ ort, kosten }) => {
                          const max = lvKostenByOrt[0].kosten;
                          return (
                            <Box key={ort}>
                              <Box sx={{ display:'flex', justifyContent:'space-between', mb:0.3 }}>
                                <Typography variant="caption">{ort}</Typography>
                                <Typography variant="caption" fontWeight={700}>{fmtEur(kosten)}</Typography>
                              </Box>
                              <LinearProgress variant="determinate" value={(kosten/max)*100}
                                sx={{ height:6, borderRadius:3, bgcolor:'#e8f5e9', '& .MuiLinearProgress-bar':{ bgcolor:GREEN } }} />
                            </Box>
                          );
                        })}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          )}

          <Card>
            <CardContent>
              {filteredLVs.length === 0 ? (
                <Alert severity="info">Keine Leistungsverzeichnisse für die gewählten Filter.</Alert>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ '& th':{ bgcolor:GREEN, color:'white', fontWeight:700, fontSize:'0.75rem' } }}>
                        <TableCell>Bezeichnung</TableCell><TableCell>Ort</TableCell><TableCell>Firma</TableCell>
                        <TableCell>Zeitraum</TableCell><TableCell>Pos.</TableCell>
                        <TableCell>Gesamtkosten</TableCell><TableCell>Kostenart</TableCell>
                        <TableCell>Status</TableCell><TableCell>Erstellt von</TableCell>
                        <TableCell align="center">PDF</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredLVs.map((lv) => {
                        const cfg = statusConfig[lv.status] || statusConfig.offen;
                        return (
                          <TableRow key={lv.id} hover>
                            <TableCell><Typography variant="body2" fontWeight={700} sx={{ fontSize:'0.85rem' }}>{lv.bezeichnung}</Typography></TableCell>
                            <TableCell sx={{ fontSize:'0.78rem' }}>{lv.ort||'–'}</TableCell>
                            <TableCell sx={{ fontSize:'0.78rem' }}>{lv.firma||'–'}</TableCell>
                            <TableCell sx={{ fontSize:'0.75rem', whiteSpace:'nowrap' }}>
                              {lv.zeitraum_von?fmtDate(lv.zeitraum_von):'–'}
                              {lv.zeitraum_bis?` – ${fmtDate(lv.zeitraum_bis)}`:''}
                            </TableCell>
                            <TableCell>
                              <Chip label={lv.anzahl_positionen} size="small" sx={{ fontSize:'0.7rem', bgcolor:'#4a7c5915', color:GREEN }} />
                            </TableCell>
                            <TableCell sx={{ fontSize:'0.82rem', fontWeight:600 }}>
                              {fmtEur(parseFloat(String(lv.gesamtkosten||0)))}
                            </TableCell>
                            <TableCell>
                              <Chip label={lv.kosten_modus==='gesamt'?'Pauschal':'Einzeln'}
                                size="small" variant="outlined" sx={{ fontSize:'0.65rem', height:18 }} />
                            </TableCell>
                            <TableCell>
                              <Chip label={cfg.label} size="small"
                                sx={{ fontSize:'0.72rem', bgcolor:cfg.color+'20', color:cfg.color, fontWeight:700 }} />
                            </TableCell>
                            <TableCell sx={{ fontSize:'0.75rem', color:'text.secondary' }}>
                              {lv.created_by_name}<br />
                              <span style={{ fontSize:'0.7rem' }}>{fmtDate(lv.created_at)}</span>
                            </TableCell>
                            <TableCell align="center">
                              <Button
                                size="small"
                                onClick={() => handlePdfLv(lv.id, lv.bezeichnung)}
                                disabled={pdfLoading}
                                sx={{ minWidth: 'auto', p: 0.5, color: GREEN }}
                                title="PDF-Report erstellen"
                              >
                                <PictureAsPdf fontSize="small" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {/* ─── Tab 3: Verkehrssicherheit ─────────────────────────────────────── */}
      {tab === 2 && (
        <Box>
          {!vsData ? (
            <Typography color="text.secondary">Verkehrssicherheits-Daten werden geladen…</Typography>
          ) : (
            <>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} md={3}><StatCard label="Bäume (nicht gefällt)" value={String(vsData.gesamt)} icon={<Park />} /></Grid>
                <Grid item xs={6} md={3}><StatCard label="Kontrolliert" value={String(vsData.kontrolliert)}
                  sub={vsData.gesamt > 0 ? `${Math.round(vsData.kontrolliert / vsData.gesamt * 100)} % erfasst` : undefined}
                  icon={<GppGood />} color="#2e7d32" /></Grid>
                <Grid item xs={6} md={3}><StatCard label="Kontrolle überfällig" value={String(vsData.ueberfaellig)}
                  icon={<Warning />} color={vsData.ueberfaellig > 0 ? '#e65100' : '#2e7d32'} /></Grid>
                {(() => {
                  const nv = (vsData.verteilung.find((v: any) => v.wert === 'Nicht gegeben')?.anzahl) || 0;
                  return <Grid item xs={6} md={3}><StatCard label="Nicht verkehrssicher" value={String(nv)}
                    icon={<ReportProblem />} color={nv > 0 ? '#c62828' : '#2e7d32'} /></Grid>;
                })()}
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%' }}><CardContent>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>Verkehrssicherheit – Verteilung</Typography>
                    <Stack spacing={1}>
                      {vsData.verteilung.map((v: any) => {
                        const max = Math.max(...vsData.verteilung.map((x: any) => x.anzahl), 1);
                        const col = VS_COLORS[v.wert] || '#90a4ae';
                        return (
                          <Box key={v.wert}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                              <Typography variant="caption">{v.wert}</Typography>
                              <Typography variant="caption" fontWeight={700}>{v.anzahl}</Typography>
                            </Box>
                            <Box sx={{ height: 8, borderRadius: 4, bgcolor: 'action.hover', overflow: 'hidden' }}>
                              <Box sx={{ height: '100%', width: `${v.anzahl / max * 100}%`, bgcolor: col }} />
                            </Box>
                          </Box>
                        );
                      })}
                    </Stack>
                  </CardContent></Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%' }}><CardContent>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>Nicht verkehrssicher – pro Ort</Typography>
                    {vsData.nichtVerkehrssicherProOrt.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">Keine – alle kontrollierten Bäume verkehrssicher.</Typography>
                    ) : (
                      <Stack spacing={1}>
                        {vsData.nichtVerkehrssicherProOrt.map((o: any) => {
                          const max = vsData.nichtVerkehrssicherProOrt[0].anzahl;
                          return (
                            <Box key={o.ort}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                                <Typography variant="caption">{o.ort}</Typography>
                                <Typography variant="caption" fontWeight={700}>{o.anzahl}</Typography>
                              </Box>
                              <Box sx={{ height: 8, borderRadius: 4, bgcolor: 'action.hover', overflow: 'hidden' }}>
                                <Box sx={{ height: '100%', width: `${o.anzahl / max * 100}%`, bgcolor: '#c62828' }} />
                              </Box>
                            </Box>
                          );
                        })}
                      </Stack>
                    )}
                  </CardContent></Card>
                </Grid>

                {vsData.kontrollenProMonat.length > 0 && (
                  <Grid item xs={12}>
                    <Card><CardContent>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Kontrollen pro Monat (letzte 12 Monate)</Typography>
                      <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={vsData.kontrollenProMonat}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} />
                          <XAxis dataKey="monat" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                          <RechartTooltip />
                          <Bar dataKey="anzahl" fill={GREEN} name="Kontrollen" radius={[3, 3, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent></Card>
                  </Grid>
                )}
              </Grid>
            </>
          )}
        </Box>
      )}

      {/* Snackbar für PDF-Erstellung */}
      <Snackbar
        open={!!snackbar}
        autoHideDuration={3500}
        onClose={() => setSnackbar('')}
        message={snackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
